import { useMutation, useQueryClient } from '@tanstack/vue-query'
import apis from '@/services'

export function useDeleteAssignment() {
  const queryClient = useQueryClient()
  const {
    isPending,
    isSuccess,
    isError,
    mutate: deleteAssignment,
  } = useMutation({
    mutationFn: async ({ accommodationId }) => await apis.admin.deleteAssignment({ accommodationId }),
    onSuccess: (_, mutationInput) => {
      queryClient.removeQueries({ queryKey: ['accommodation', mutationInput.accommodationId] })
      queryClient.removeQueries({ queryKey: ['paidSubscriptions', mutationInput.accommodationId] })
    },
  })

  return {
    isPending,
    isSuccess,
    isError,
    deleteAssignment,
  }
}

export function useInviteUserInAdminTool() {
  const {
    isPending,
    isSuccess,
    isError,
    mutate: inviteUserAsAdmin,
  } = useMutation({
    mutationFn: ({ organisationId, data }) => apis.admin.inviteUserAsAdmin({ organisationId, data }),
  })

  return {
    isPending,
    isSuccess,
    isError,
    inviteUserAsAdmin,
  }
}

export function useJoinOrganisationInAdminTool() {
  const {
    isPending,
    isSuccess,
    isError,
    mutate: joinOrganisationAsSupport,
  } = useMutation({
    mutationFn: ({ organisationId }) => apis.admin.joinOrganisationAsSupport({ organisationId }),
  })

  return {
    isPending,
    isSuccess,
    isError,
    joinOrganisationAsSupport,
  }
}

export function useFutureUsages() {
  const {
    isPending,
    isSuccess,
    isError,
    mutate: uploadFutureUsages,
  } = useMutation({
    // The admin search results don't include the Rate Connect zuoraSubscriptionName,
    // so it's looked up via the accommodation's paid subscriptions before uploading.
    mutationFn: async ({ accommodationId }) => {
      const { data } = await apis.premium.getPaidSubscriptions(accommodationId)
      const activeRateConnectCampaign = data?.campaigns?.find(
        campaign => campaign.productName === 'Rate Connect' && !campaign.endDate
      )

      if (!activeRateConnectCampaign?.zuoraSubscriptionName) {
        throw new Error('No active Rate Connect subscription found for this accommodation')
      }

      return apis.premium.uploadFutureUsages({ subscriptionName: activeRateConnectCampaign.zuoraSubscriptionName })
    },
  })

  return {
    isPending,
    isSuccess,
    isError,
    uploadFutureUsages,
  }
}

export function useApplyVoucher() {
  const {
    isLoading,
    isSuccess,
    isError,
    mutate: applyVoucher,
  } = useMutation({
    mutationFn: ({ accommodationId, voucherCode }) => apis.admin.addVoucher({ accommodationId, voucherCode }),
  })

  return {
    isLoading,
    isSuccess,
    isError,
    applyVoucher,
  }
}

export function useApplyRCVoucher() {
  const {
    isLoading,
    isSuccess,
    isError,
    mutate: applyRCVoucher,
  } = useMutation({
    mutationFn: ({ accommodationId, voucherCode }) => apis.admin.addRCVoucher({ accommodationId, voucherCode }),
  })

  return {
    isLoading,
    isSuccess,
    isError,
    applyRCVoucher,
  }
}

export function useKickUserFromOrganisation() {
  const {
    isPending,
    isSuccess,
    isError,
    mutate: kickUserFromOrganisation,
  } = useMutation({
    mutationFn: ({ organisationId, userId }) => apis.admin.kickUserFromOrganisation({ organisationId, userId }),
  })

  return {
    isPending,
    isSuccess,
    isError,
    kickUserFromOrganisation,
  }
}
